/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { getTenantLogoUrl } from "../../utils/getTenantLogoUrl";
import { sanitizeStoreSlug, isAllowedSlugInputChar } from "../../utils/sanitizeStoreSlug";
import { useAuth } from "../../context/auth/AuthContext";
import { useStore } from "../../context/store/StoreContext";
import { invalidateTenantPlanCache } from "../../admin_store/utils/subscriptionPlan";
import {
  clearCompanyInfoDraft,
  clearOnboardingSession,
  dataUrlToFile,
  fileToDataUrl,
  loadCompanyInfoDraft,
  parseStorageJSON,
  recoverTenantAuth,
  saveCompanyInfoDraft,
  setOnboardingStep,
} from "../utils/onboardingSession";
import { goToLoginScreen } from "../../navigation/limitedBackHistory";

export default function useCompanyInfo(isArabic, navigate) {
  const { logout, login: authLogin } = useAuth();
  const { clearStoreData } = useStore();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);

  const [form, setForm] = useState({
    logo: null,
    storeName: "",
    storeSlug: "",
    sector: "",
    paymentMethods: [],
    services: "",
  });

  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const slugInputRef = useRef(null);
  const slugComposingRef = useRef(false);
  const logoDataUrlRef = useRef("");

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");

  /* ----------------------------------------------------
        (1) HANDLE SCREEN SIZE
  ---------------------------------------------------- */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ----------------------------------------------------
        (2) PREFILL COMPANY INFO FROM LOCALSTORAGE
        Supports:
        - Login response
        - Register response
        - Refresh / resume draft
  ---------------------------------------------------- */
  useEffect(() => {
    recoverTenantAuth(authLogin);
    setOnboardingStep("company-info");

    try {
      const draft = loadCompanyInfoDraft() || {};
      const reg = parseStorageJSON(localStorage.getItem("fullRegisterResponse"), {});
      const login = parseStorageJSON(localStorage.getItem("fullLoginResponse"), {});
      const tenant =
        parseStorageJSON(localStorage.getItem("tenant"), null) ||
        reg.tenant ||
        login.tenant ||
        {};
      const tenantDetails =
        parseStorageJSON(localStorage.getItem("tenantDetails"), null) ||
        login.tenantDetails ||
        {};

      const storedMethods = parseStorageJSON(localStorage.getItem("companyPaymentMethods"), []);
      const logoDataUrl = draft.logoDataUrl || "";
      logoDataUrlRef.current = logoDataUrl;
      const restoredLogo = dataUrlToFile(logoDataUrl);

      setForm({
        logo: restoredLogo,
        storeName:
          draft.storeName ||
          tenantDetails.clientName ||
          tenantDetails.ClientName ||
          localStorage.getItem("companyStoreName") ||
          "",
        storeSlug: sanitizeStoreSlug(
          draft.storeSlug ||
            tenantDetails.storeSlug ||
            tenantDetails.StoreSlug ||
            localStorage.getItem("companyStoreSlug") ||
            "",
          { finalize: true }
        ),
        sector:
          draft.sector ||
          tenantDetails.sectorType ||
          tenantDetails.SectorType ||
          localStorage.getItem("companySector") ||
          "",
        paymentMethods:
          (draft.paymentMethods && draft.paymentMethods.length
            ? draft.paymentMethods
            : null) ||
          tenantDetails.paymentMethods ||
          storedMethods ||
          [],
        services:
          draft.services ||
          tenantDetails.businessName ||
          tenantDetails.BusinessName ||
          localStorage.getItem("companyServices") ||
          "",
      });

      const logoUrl = getTenantLogoUrl(tenantDetails.logo || tenant.logo || tenant.Logo);
      setPreview(draft.preview || logoDataUrl || logoUrl || null);
    } catch (err) {
      console.error("Error loading company info:", err);
    }
  }, []);

  /* ----------------------------------------------------
        TOAST HELPERS
  ---------------------------------------------------- */
  const showError = (msg) =>
    toast.error(msg, { position: "top-center", rtl: isArabic });

  const showSuccess = (msg) =>
    toast.success(msg, { position: "top-center", rtl: isArabic });

  /* ----------------------------------------------------
        FORM HANDLERS
  ---------------------------------------------------- */
  const persistDraft = (nextForm, nextPreview, logoDataUrl) => {
    saveCompanyInfoDraft({
      ...nextForm,
      preview: nextPreview,
      logoDataUrl: logoDataUrl || logoDataUrlRef.current || "",
    });
  };

  const applySlugValue = (raw, { finalize = false } = {}) => {
    const next = slugComposingRef.current && !finalize
      ? raw
      : sanitizeStoreSlug(raw, { finalize });

    setForm((prev) => (prev.storeSlug === next ? prev : { ...prev, storeSlug: next }));

    // Keep the DOM value in sync when sanitization strips characters.
    // On iOS/Android, resetting a controlled input to the same React state
    // without updating the native value freezes the virtual keyboard.
    const el = slugInputRef.current;
    if (el && el.value !== next) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const delta = el.value.length - next.length;
      el.value = next;
      if (typeof start === "number" && typeof end === "number") {
        const caret = Math.max(0, start - delta);
        try {
          el.setSelectionRange(caret, caret);
        } catch {
          /* some mobile browsers reject setSelectionRange on url inputs */
        }
      }
    }

    return next;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "storeSlug") {
      const next = applySlugValue(value);
      persistDraft({ ...form, storeSlug: next }, preview);
      return;
    }

    setForm((prev) => {
      const next = { ...prev, [name]: value };
      persistDraft(next, preview);
      return next;
    });
  };

  const handleSlugBeforeInput = (e) => {
    if (e.data == null) return;
    if (!isAllowedSlugInputChar(e.data)) e.preventDefault();
  };

  const handleSlugCompositionStart = () => {
    slugComposingRef.current = true;
  };

  const handleSlugCompositionEnd = (e) => {
    slugComposingRef.current = false;
    const next = applySlugValue(e.target.value);
    persistDraft({ ...form, storeSlug: next }, preview);
  };

  const handleSlugBlur = (e) => {
    const next = applySlugValue(e.target.value, { finalize: true });
    persistDraft({ ...form, storeSlug: next }, preview);
  };

  const handleSlugPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData?.getData("text") || "";
    const el = slugInputRef.current;
    const current = form.storeSlug || "";
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? start;
    const next = applySlugValue(current.slice(0, start) + pasted + current.slice(end));
    persistDraft({ ...form, storeSlug: next }, preview);
  };

  const handleLogoUpload = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showError(isArabic ? "يرجى اختيار صورة فقط" : "Please select an image only");
      return;
    }

    setForm((prev) => ({ ...prev, logo: file }));
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    fileToDataUrl(file)
      .then((dataUrl) => {
        logoDataUrlRef.current = dataUrl;
        persistDraft({ ...form, logo: null }, objectUrl, dataUrl);
      })
      .catch(() => {});
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    handleLogoUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleLogoUpload(file);
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, logo: null }));
    setPreview(null);
    logoDataUrlRef.current = "";
    persistDraft({ ...form, logo: null }, "", "");

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePaymentChange = (e) => {
    const { value, checked } = e.target;
    setForm((prev) => {
      const next = {
        ...prev,
        paymentMethods: checked
          ? [...prev.paymentMethods, value]
          : prev.paymentMethods.filter((m) => m !== value),
      };
      persistDraft(next, preview);
      return next;
    });
  };

  /* ----------------------------------------------------
        SUBMIT
  ---------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!preview && !form.logo && !logoDataUrlRef.current)
      return showError(isArabic ? "الرجاء رفع شعار المتجر 📸" : "Please upload your store logo 📸");

    if (!form.storeName.trim())
      return showError(isArabic ? "الرجاء إدخال اسم المتجر 🏪" : "Please enter your store name 🏪");

    const finalSlug = sanitizeStoreSlug(form.storeSlug, { finalize: true });
    if (!finalSlug)
      return showError(isArabic ? "الرجاء إدخال رابط المتجر (Slug) 🔗" : "Please enter your store slug 🔗");
    if (finalSlug !== form.storeSlug) {
      setForm((prev) => ({ ...prev, storeSlug: finalSlug }));
    }

    if (!form.sector.trim())
      return showError(isArabic ? "الرجاء إدخال نوع القطاع 🏷️" : "Please enter your business sector 🏷️");

    if (form.paymentMethods.length === 0)
      return showError(isArabic ? "اختر طريقة دفع واحدة على الأقل 💳" : "Select at least one payment method 💳");

    if (!form.services.trim())
      return showError(isArabic ? "أدخل الخدمات المقدمة ✍️" : "Please describe your services ✍️");

    setLoading(true);

    try {
      const tenant = JSON.parse(localStorage.getItem("tenant")) || {};
      const tenantDetails = JSON.parse(localStorage.getItem("tenantDetails")) || {};
      const currentTenantId = tenant.tenantId || tenantDetails.tenantId || 0;

      // Validate if slug already exists
      try {
        const validationRes = await fetch(`${baseUrl}/GetAllTenantFullDetails?tenantId=-1`);
        if (validationRes.ok) {
          const allTenants = await validationRes.json();
          const existing = allTenants.find(t =>
            t.TenantId !== currentTenantId &&
            ((t.storeSlug && t.storeSlug.toLowerCase() === finalSlug) ||
              (t.StoreSlug && t.StoreSlug.toLowerCase() === finalSlug))
          );
          if (existing) {
            setLoading(false);
            return showError(isArabic ? "اسم الرابط (Slug) مستخدم مسبقاً، يرجى اختيار اسم آخر ❌" : "This store slug is already used by another store, please choose another one ❌");
          }
        }
      } catch (err) {
        console.error("Failed to validate slug", err);
      }

      const fd = new FormData();

      // ✅ 🚦 تحسين عملية إرسال البيانات لضمان عدم ضيابها أو إرسال null
      // نأخذ البيانات من tenant أولاً (لأنها الأحدث بعد الـ Register) ثم tenantDetails
      const finalTenantId = tenant.tenantId || tenantDetails.tenantId || 0;
      const finalTenantName = tenant.tenantName || tenantDetails.tenantName || tenantDetails.companyName || "";
      const finalEngTenantName = tenant.engTenantName || tenantDetails.engTenantName || finalTenantName;
      const finalEmail = tenant.email || tenantDetails.email || "";
      const finalPhone = tenant.phone || tenantDetails.phone || "";
      const finalCreatedAt = tenant.createdAt || tenant.CreatedAt || tenantDetails.createdAt || new Date().toISOString();

      fd.append("TenantId", finalTenantId);
      fd.append("TenantName", finalTenantName);
      fd.append("EngTenantName", finalEngTenantName);
      fd.append("Email", finalEmail);
      fd.append("Phone", finalPhone);
      fd.append("SubDomain", tenant.subDomain || tenantDetails.subDomain || "");
      fd.append("IsActive", tenant.isActive !== undefined ? tenant.isActive : 1);
      fd.append("CreatedAt", finalCreatedAt);
      fd.append("Product", tenant.product || tenantDetails.product || "");
      fd.append("TenantDtlId", tenantDetails.tenantDtlId || 0);

      // Logo
      const logoFile = form.logo instanceof File
        ? form.logo
        : dataUrlToFile(logoDataUrlRef.current);
      if (logoFile) fd.append("LogoFile", logoFile);

      // Updated company info
      fd.append("ClientName", form.storeName);
      fd.append("SectorType", form.sector);
      fd.append("BusinessName", form.services);
      fd.append("CompanyName", finalTenantName);
      fd.append("StoreSlug", finalSlug);

      // ✅ الحفاظ على حقول الخطة والاشتراك (حتى لو فارغة حالياً) لمنع مسح الحقول في الـ API
      fd.append("MainPlanId", tenantDetails.mainPlanId || "");
      fd.append("PlanType", tenantDetails.planType || "");
      fd.append("Price", tenantDetails.price || 0);
      fd.append("Descripsion", tenantDetails.descripsion || "");
      fd.append("EngPlanType", tenantDetails.engPlanType || "");
      fd.append("EngDescripsion", tenantDetails.engDescripsion || "");

      // Notes 1-8 (إرسالها فارغة إذا لم تكن موجودة لمنع الـ Null في القاعدة)
      for (let i = 1; i <= 8; i++) {
        fd.append(`Note${i}`, tenantDetails[`note${i}`] || "");
      }

      // Payment Methods
      fd.append("PaymentMethods", JSON.stringify(form.paymentMethods));

      const authToken = localStorage.getItem("authToken") || localStorage.getItem("token");

      const res = await fetch(`${baseUrl}/UpdateTenantDetails/UpdateTenantFullDetails`, {
        method: "POST",
        headers: { "Authorization": authToken ? `Bearer ${authToken}` : "" },
        body: fd,
      });

      const resData = await res.json().catch(() => ({}));

      if (!res.ok || resData.success === false) {
        throw new Error(resData.message || resData.Message || "Error updating company info");
      }

      // Save updated tenantDetails (Merge with existing to prevent data loss)
      const mergedDetails = { ...tenantDetails, ...resData };
      localStorage.setItem("tenantDetails", JSON.stringify(mergedDetails));
      // Save for ThemePlan validation
      localStorage.setItem("companyStoreName", form.storeName);
      localStorage.setItem("companyStoreSlug", finalSlug);
      localStorage.setItem("companySector", form.sector);
      localStorage.setItem("companyServices", form.services);
      localStorage.setItem("companyPaymentMethods", JSON.stringify(form.paymentMethods));

      // 2️⃣ حفظ طرق الدفع باستخدام الـ API المخصص (SavePaymentMethods) لضمان تسجيلها
      try {
        const gRes = await fetch(`${baseUrl}/PaymentMethods/GetPaymentMethods?TenantId=-1`, {
          headers: { "Authorization": authToken ? `Bearer ${authToken}` : "" }
        });
        if (gRes.ok) {
          const allGlobal = await gRes.json();
          const pMethodsPayload = (form.paymentMethods || []).map(selectedName => {
            const found = Array.isArray(allGlobal) && allGlobal.find(g =>
              (g.paymentMethodName || "").toLowerCase().includes(selectedName.toLowerCase()) ||
              (g.engPaymentMethodName || "").toLowerCase().includes(selectedName.toLowerCase())
            );
            return {
              tenantId: parseInt(finalTenantId),
              paymentMethodId: found ? (found.paymentMethodId || found.id) : 0,
              paymentMethodName: selectedName,
              engPaymentMethodName: selectedName,
              isActive: 1
            };
          });

          if (pMethodsPayload.length > 0) {
            await fetch(`${baseUrl}/PaymentMethods/SavePaymentMethods`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": authToken ? `Bearer ${authToken}` : ""
              },
              body: JSON.stringify(pMethodsPayload)
            });
          }
        }
      } catch (pmErr) {
        console.error("Payment save failed:", pmErr);
      }

      clearCompanyInfoDraft();
      setOnboardingStep("theme-plan");

      showSuccess(isArabic ? "تم حفظ البيانات بنجاح ✅" : "Data saved successfully ✅");

      setTimeout(() => navigate("/theme-plan"), 1200);

    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    clearStoreData();
    logout();
    invalidateTenantPlanCache();
    sessionStorage.removeItem("trialExpiredAlertShown");
    sessionStorage.removeItem("paidExpiredAlertShown");

    [
      "fullLoginResponse", "fullRegisterResponse", "token", "authToken", "authRole",
      "user", "isTenantOwner", "loginMessage",
      "tenant", "tenantDetails", "plans", "themes", "paymentMethods",
      "tenantUsersList", "tenantId", "companyStoreSlug", "companyStoreName",
      "companySector", "companyServices", "companyPaymentMethods", "storeData",
      "permissions", "subscriptionStartDate", "paidStartDate", "trialStartDate",
      "subscriptionDurationDays", "plan_expiry_date", "plan_renewed_at",
      "trialUsed", "force_expiry_notice", "plan_duration_type",
      "subscription", "subscriptionExpired", "isTrial",
    ].forEach((key) => localStorage.removeItem(key));

    clearOnboardingSession();
    goToLoginScreen(navigate);
  };

  return {
    loading,
    isMobile,
    form,
    preview,
    isDragging,
    fileInputRef,
    slugInputRef,
    handleSlugBeforeInput,
    handleSlugCompositionStart,
    handleSlugCompositionEnd,
    handleSlugBlur,
    handleSlugPaste,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInputChange,
    handleRemoveImage,
    handleChange,
    handlePaymentChange,
    handleSubmit,
    handleGoToLogin,
  };
}
